import type { JsonRecord } from "../../types";

export type ExecutionTypeKey =
  | "concreto"
  | "terracerias"
  | "acero_refuerzo"
  | "acero_estructural"
  | "acero_presfuerzo"
  | "petreos_suelos"
  | "prefabricados"
  | "asfaltos"
  | "vias_rieles"
  | "vias_durmientes"
  | "vias_fijaciones"
  | "vias_soldadura"
  | "vias_aparatos"
  | "vias_senalamiento";

export type ExecutionOperation = "listar" | "insertar" | "editar" | "eliminar" | "catalogos" | "validar";

export interface ExecutionField {
  key: string;
  label: string;
  type?: "text" | "date" | "number" | "boolean" | "front" | "subfront" | "select";
  required?: boolean;
  readOnly?: boolean;
  options?: { value: string; label: string }[];
}

export interface ExecutionTypeConfig {
  key: ExecutionTypeKey;
  label: string;
  group?: string;
  enabled: boolean;
  dateKey?: string;
  columns: { key: string; label: string }[];
  detailFields: { key: string; label: string }[];
  fields: ExecutionField[];
}

export interface ConcreteExecutionRow extends JsonRecord {
  frente?: string | null;
  subfrente?: string | null;
  tipo_mezcla_label?: string | null;
  especificacion?: string | null;
  resistencia_unidad_simbolo?: string | null;
  area?: string | null;
  muestras_resistencia_contratista?: number | null;
  muestras_resistencia_supervision?: number | null;
  pruebas_fresco_supervision?: number | null;
}

export interface EarthworksExecutionRow extends JsonRecord {
  frente?: string | null;
  subfrente?: string | null;
  estructura?: string | null;
  area_nombre?: string | null;
}

export type StructuralSteelProduct = "Barra" | "Perfil" | "Placa" | "Tablaestaca" | "Otro";
export type StructuralSteelSection = "Circular" | "Cuadrada" | "Hexagonal";

export interface StructuralSteelExecutionRow extends JsonRecord {
  fecha_suministro?: string | null;
  folio_remision_proveedor?: string | null;
  folio_nota_recepcion?: string | null;
  producto?: StructuralSteelProduct | string | null;
  especificacion?: string | null;
  grado?: string | null;
  designacion_renderizada?: string | null;
  cantidad_renderizada?: string | null;
  puede_editar?: boolean;
  puede_eliminar?: boolean;
}

const pending = (
  key: ExecutionTypeKey,
  label: string,
  group?: string,
): ExecutionTypeConfig => ({
  key,
  label,
  group,
  enabled: false,
  columns: [],
  detailFields: [],
  fields: [],
});

export const executionTypes: ExecutionTypeConfig[] = [
  {
    key: "concreto",
    label: "Concreto",
    enabled: true,
    dateKey: "fecha",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "frente", label: "Frente" },
      { key: "subfrente", label: "Subfrente" },
      { key: "elemento", label: "Elemento" },
      { key: "ubicacion", label: "Ubicación" },
      { key: "volumen_renderizado", label: "Volumen" },
      { key: "resistencia_renderizada", label: "Resistencia" },
    ],
    detailFields: [
      { key: "tipo_mezcla_label", label: "Tipo de mezcla" },
      { key: "proveedor", label: "Proveedor" },
      { key: "especificacion", label: "Especificación" },
      { key: "estado_revision", label: "Estado de revisión" },
      { key: "comentarios", label: "Comentarios" },
    ],
    fields: [
      { key: "fecha", label: "Fecha de colado", type: "date", required: true },
      { key: "frente_id", label: "Frente", type: "front", required: true },
      { key: "subfrente_id", label: "Subfrente", type: "subfront" },
      { key: "elemento", label: "Elemento", required: true },
      { key: "ubicacion", label: "Ubicación" },
      { key: "proveedor", label: "Proveedor" },
      { key: "actividad_material_id", label: "Tipo de mezcla", type: "select", required: true },
      { key: "especificacion_id", label: "Especificación", type: "select", required: true },
      { key: "resistencia", label: "Resistencia", type: "number", required: true },
      { key: "volumen", label: "Volumen (m³)", type: "number" },
      { key: "resistencia_unidad_id", label: "Unidad", type: "select", required: true },
      { key: "cantidad_entregas", label: "Bachas / ollas", type: "number" },
      { key: "garantia", label: "Garantía (días)", type: "number" },
      { key: "muestras_co", label: "Muestras del contratista", type: "number" },
      { key: "ctrl_fresco_co", label: "El contratista ejecutó las pruebas en estado fresco", type: "boolean" },
      { key: "sup_ext", label: "Trabajos dentro de los alcances de la supervisión externa", type: "boolean" },
      { key: "comentarios", label: "Comentarios" },
    ],
  },
  {
    key: "terracerias",
    label: "Terracerías",
    enabled: true,
    dateKey: "fecha_termino",
    columns: [
      { key: "fecha_termino", label: "Fecha" },
      { key: "frente", label: "Frente" },
      { key: "subfrente", label: "Subfrente" },
      { key: "estructura", label: "Estructura" },
      { key: "ubicacion_renderizada", label: "Ubicación" },
      { key: "capa", label: "Capa" },
    ],
    detailFields: [
      { key: "area_nombre", label: "Área" },
      { key: "comentarios", label: "Comentarios" },
    ],
    fields: [
      { key: "fecha_termino", label: "Fecha de término", type: "date", required: true },
      { key: "frente_id", label: "Frente", type: "front", required: true },
      { key: "subfrente_id", label: "Subfrente", type: "subfront" },
      {
        key: "tipo_obra",
        label: "Tipo de obra",
        type: "select",
        required: true,
        options: [
          { value: "Acotado", label: "Acotado" },
          { value: "Lineal", label: "Lineal" },
          { value: "Puntual", label: "Puntual" },
        ],
      },
      { key: "capa", label: "Capa / trabajo", required: true },
      { key: "cad_inicial", label: "Estación inicial", type: "number" },
      { key: "cad_final", label: "Estación final", type: "number" },
      { key: "franja", label: "Franja" },
      { key: "punto_inicial", label: "Punto inicial" },
      { key: "punto_final", label: "Punto final" },
      { key: "ubicacion_puntual", label: "Ubicación puntual" },
      { key: "area", label: "Área", type: "number" },
      { key: "longitud_tramo_drenaje", label: "Longitud tramo de drenaje", type: "number" },
      { key: "longitud", label: "Longitud calculada", type: "number", readOnly: true },
      { key: "ubicacion", label: "Ubicación calculada", readOnly: true },
      { key: "comentarios", label: "Comentarios" },
    ],
  },
  pending("acero_refuerzo", "Refuerzo", "Acero"),
  {
    key: "acero_estructural",
    label: "Estructural",
    group: "Acero",
    enabled: true,
    dateKey: "fecha_suministro",
    columns: [
      { key: "fecha_suministro", label: "Fecha" },
      { key: "producto", label: "Producto" },
      { key: "especificacion", label: "Especificación" },
      { key: "grado", label: "Grado" },
      { key: "designacion_renderizada", label: "Designación / dimensiones" },
      { key: "cantidad_renderizada", label: "Cantidad" },
      { key: "proveedor", label: "Proveedor" },
    ],
    detailFields: [],
    fields: [],
  },
  pending("acero_presfuerzo", "Presfuerzo", "Acero"),
  pending("petreos_suelos", "Pétreos y suelos"),
  pending("prefabricados", "Prefabricados"),
  pending("asfaltos", "Asfaltos"),
  pending("vias_rieles", "Rieles", "Vías"),
  pending("vias_durmientes", "Durmientes", "Vías"),
  pending("vias_fijaciones", "Fijaciones", "Vías"),
  pending("vias_soldadura", "Soldadura", "Vías"),
  pending("vias_aparatos", "Aparatos", "Vías"),
  pending("vias_senalamiento", "Señalamiento", "Vías"),
];

export type ExecutionSelectorNode =
  | { kind: "item"; label: string; item: ExecutionTypeConfig }
  | { kind: "group"; label: string; items: ExecutionTypeConfig[] };

const compareSpanish = (a: string, b: string) => a.localeCompare(b, "es", { sensitivity: "base" });

const ungrouped = executionTypes
  .filter((item) => !item.group)
  .map((item): ExecutionSelectorNode => ({ kind: "item", label: item.label, item }));

const groupLabels = [...new Set(executionTypes.map((item) => item.group).filter(Boolean) as string[])];
const groups = groupLabels.map((label): ExecutionSelectorNode => ({
  kind: "group",
  label,
  items: executionTypes
    .filter((item) => item.group === label)
    .sort((a, b) => compareSpanish(a.label, b.label)),
}));

export const executionSelectorNodes = [...ungrouped, ...groups]
  .sort((a, b) => compareSpanish(a.label, b.label));

export interface ExecutionListResult {
  rows: JsonRecord[];
  total: number;
  fronts: JsonRecord[];
  subfronts: JsonRecord[];
  canInsert: boolean;
}
